/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { useFeatureFlags } from '@kaiizen/kai-pro-api-react-state';
import {
  getEffectiveScanValue,
  type ScanGroupOptimized,
  type ScanTaskOptimized,
} from '@kaiizen/kai-scan-template-system';
import { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router';

import { isOptedIntoNewExperience } from '@/Client/Lib/newExperienceLib';
import {
  DEFAULT_NEEDS_ATTENTION_TITLE,
  type ScopeAttentionFilterContext,
  taskMatchesScopeAttentionFilter,
  taskNeedsAttentionFlag,
} from '@/Client/Lib/scopeAttentionFilterLib';
import { getPartCostValue, getTaskTitle } from '@/Client/Lib/scopeNavigatorPartPresentersLib';
import { formatUsd, getTaskModifiers } from '@/Client/Presenters';
import { type TaskModifier, useScanEditingContextSlice } from '@/Client/State';
import { getScanUrl } from '@/Client/State/kaiNavigation';
import { styleguideColors as COLORS, styleguideFontSize as FONT_SIZE, styleguideFontWeight as FONT_WEIGHT } from '@/Client/Styles/kaiShikiTokens';
import { NeedsAttentionIndicator, TaskStatusBadge } from '@/Client/Widgets';
import { styleguideFontFamily as FONT } from '@kaiizen/kai-shiki/core';

import { pxToRem } from '@/Client/Styles/pxToRem';
import { truncateGroupTaskListName } from './focusedGroupTaskListLib';

const TASK_GRID_COLUMNS_STYLE = css`
  display: grid;
  grid-template-columns: minmax(0, 1.5fr) 116px minmax(0, 0.9fr) minmax(0, 1.2fr) 42px 64px 56px 82px;
  gap: 10px;
  align-items: center;
  padding: 11px 12px;
`;

const TASK_LIST_STYLE = css`
  label: FOCUSED_TASK_LIST;
  display: flex;
  flex-direction: column;
  background: ${COLORS.white};
  border: 1px solid ${COLORS.stroke};
`;

const TASK_HEAD_STYLE = css`
  ${TASK_GRID_COLUMNS_STYLE};
  label: FOCUSED_TASK_LIST_HEAD;
  padding: 8px 12px;
  border-bottom: 1px solid ${COLORS.stroke};
  background: ${COLORS.sunken};
  font-family: ${FONT.mono};
  font-size: ${pxToRem(FONT_SIZE.monoMicro)};
  font-weight: ${FONT_WEIGHT.bold};
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: ${COLORS.textTertiary};

  span {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const TASK_ROW_STYLE = css`
  ${TASK_GRID_COLUMNS_STYLE};
  label: FOCUSED_TASK_ROW;
  border: 0;
  border-bottom: 1px solid ${COLORS.stroke};
  background: transparent;
  text-align: left;
  cursor: pointer;
  font-family: ${FONT.body};
  font-size: ${pxToRem(12)};
  transition: background 0.12s;
  width: 100%;

  &:last-child { border-bottom: 0; }
  &:hover { background: ${COLORS.sunken}; }
`;

const TASK_NAME_STYLE = css`
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  color: ${COLORS.ink};
  line-height: 1.35;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

/** NeedsAttentionIndicator renders nothing when a task carries no flag, which
 *  would otherwise pull TaskStatusBadge left by its width row to row. Sized to
 *  match the indicator's own `size` prop below rather than a second, driftable
 *  literal. */
export const NEEDS_ATTENTION_ICON_SIZE = 14;

const NEEDS_ATTENTION_SLOT_STYLE = css`
  width: ${NEEDS_ATTENTION_ICON_SIZE}px;
  flex-shrink: 0;
`;

const TASK_NAME_TEXT_STYLE = css`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
`;

const TASK_TEXT_CELL_STYLE = css`
  color: ${COLORS.textSecondary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
`;

const TASK_TEXT_CELL_NONE_STYLE = css`
  ${TASK_TEXT_CELL_STYLE};
  color: ${COLORS.textTertiary};
`;

/** Composed onto a cell's own style (last, so it wins) when that cell is the
 *  specific reason a row's NeedsAttentionIndicator is showing — the prototype's
 *  own rule: each flag colours the field it's actually about (Contractor,
 *  Product, or Amount) rather than the whole row. The doubled `&&` selector
 *  matches `UNREADY_TEXT_STYLE`'s `span { color }` rule's specificity (a class
 *  plus a type selector) so a flagged cell stays red even on an unready row —
 *  a flag must never be visually suppressed by the row's own muted dimming. */
const FLAGGED_TEXT_STYLE = css`
  && {
    color: ${COLORS.error};
    font-weight: ${FONT_WEIGHT.bold};
  }
`;

const TASK_NUM_CELL_STYLE = css`
  font-family: ${FONT.body};
  font-size: ${pxToRem(12)};
  color: ${COLORS.textSecondary};
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  text-align: right;
`;

/** Prototype `.pgd-tk-files` — "photos / notes", each count muted when zero. */
const TASK_FILES_CELL_STYLE = css`
  justify-self: end;
  font-family: ${FONT.body};
  font-size: ${pxToRem(12)};
  color: ${COLORS.textSecondary};
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  text-align: right;
`;

const FILES_SEP_STYLE = css`
  margin: 0 2px;
  color: ${COLORS.textTertiary};
`;

const FILES_ZERO_STYLE = css`
  color: ${COLORS.textTertiary};
`;

const TASK_COST_STYLE = css`
  font-weight: 500;
  color: ${COLORS.ink};
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  text-align: right;
`;

/** A draft row that can't be priced yet reads muted, per the prototype's `.is-unready`. */
const UNREADY_TEXT_STYLE = css`
  span { color: ${COLORS.textTertiary}; }
`;

const PRODUCT_LABEL_MAX_LENGTH = 24;

/**
 * The row's Product / Qty / Rate cells, read off the task's first selected option —
 * the closest thing the data has to the prototype's single product per task. Qty and
 * rate resolve only when the option holds exactly one line item, where they are
 * unambiguous; a dash never lies.
 */
const getTaskProductCells = (task: ScanTaskOptimized): { product?: string; qty?: string; rate?: string } => {
  const option = task.options?.[0];
  if (!option) return {};
  const product = option.template?.name || undefined;
  const lineItems = option.lineItems ?? [];
  if (lineItems.length !== 1) return { product };
  const quantityValue = lineItems[0].values?.quantity;
  const quantity = quantityValue == null ? undefined : Number(getEffectiveScanValue(quantityValue) ?? undefined);
  if (!quantity || !isFinite(quantity)) return { product };
  const cost = getPartCostValue(lineItems[0]);
  return {
    product,
    qty: String(quantity),
    rate: cost != null && quantity > 0 ? formatUsd(cost / quantity, false, true) : undefined,
  };
};

/**
 * Every modifier the task carries, named as the org named them, comma-joined — an em dash when it
 * carries none.
 *
 * This used to check a fixed pair and spell their labels out, which an org-created Display
 * modifier would have fallen straight through (KAIDEV-3182/4206).
 */
const getModifierLabel = (
  task: ScanTaskOptimized,
  knownTaskModifiers: readonly TaskModifier[],
): string => {
  const labels = getTaskModifiers(task, knownTaskModifiers).map(({ displayName }) => displayName);
  return labels.length ? labels.join(', ') : '—';
};

/** Prototype `.files-lgd` — the photos/notes column is captioned by its two glyphs. */
const FILES_LEGEND_STYLE = css`
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
  gap: 2px;

  svg {
    width: 12px;
    height: 12px;
    stroke: currentColor;
    fill: none;
    stroke-width: 1.2;
    flex-shrink: 0;
  }
`;

/** The column labels, shared between every table this view draws. */
export const FocusedGroupTaskListHead = () => (
  <div css={TASK_HEAD_STYLE} data-testid={'focused-task-list-head'}>
    <span>{'Task'}</span>
    <span>{'Contractor'}</span>
    <span>{'Modifier'}</span>
    <span>{'Product'}</span>
    <span style={{ textAlign: 'right' }}>Qty</span>
    <span style={{ textAlign: 'right' }}>Rate</span>
    <span css={FILES_LEGEND_STYLE} title={'Photos / notes'} aria-label={'Photos and notes'}>
      <svg viewBox={'0 0 16 16'} aria-hidden={'true'}>
        <path d={'M2 4.5h2.4l1-1.6h5.2l1 1.6H14v8H2z'} />
        <circle cx={'8'} cy={'8.4'} r={'2.2'} />
      </svg>
      /
      <svg viewBox={'0 0 16 16'} aria-hidden={'true'}>
        <path d={'M3 2.5h10v11H3z'} />
        <path d={'M5.5 6h5M5.5 8.5h5M5.5 11h3'} />
      </svg>
    </span>
    <span style={{ textAlign: 'right' }}>Amount</span>
  </div>
);

/**
 * Prototype task table — the rows a group's tasks are read in, on the scope and
 * group pages alike, each routing into the task editor on click.
 */
export const FocusedGroupTaskListView = ({
  group,
  tasks,
}: {
  group: ScanGroupOptimized;
  tasks: readonly ScanTaskOptimized[];
}) => {
  const navigate = useNavigate();
  const { id: scanId } = useParams();
  const { featureFlags } = useFeatureFlags();
  const newScopeBuilderFlow = isOptedIntoNewExperience(featureFlags);
  const {
    approvalStatusByTaskId,
    scanNotesByScanPartId,
    overallTaskStatusByTaskId,
    getContractorForTask,
    editRequestTaskIds,
    showScanChanges,
    changedScopedTaskIds,
    knownTaskModifiers,
  } = useScanEditingContextSlice([
    'approvalStatusByTaskId',
    'scanNotesByScanPartId',
    'overallTaskStatusByTaskId',
    'getContractorForTask',
    'editRequestTaskIds',
    'showScanChanges',
    'changedScopedTaskIds',
    'knownTaskModifiers',
  ]);

  const evalContext = useMemo<ScopeAttentionFilterContext>(
    () => ({
      getContractorForTask,
      editRequestTaskIds: new Set(editRequestTaskIds ?? []),
      changeOrderTaskIds: new Set(showScanChanges ? changedScopedTaskIds ?? [] : []),
    }),
    [getContractorForTask, editRequestTaskIds, showScanChanges, changedScopedTaskIds],
  );

  if (!tasks.length) return null;

  return (
    <div css={TASK_LIST_STYLE} data-testid='focused-group-task-list-view'>
      <FocusedGroupTaskListHead />
      {tasks.map(task => {
        const fullName = getTaskTitle(task, 'Untitled task');
        const displayName = truncateGroupTaskListName(fullName);
        const approvalStatus = approvalStatusByTaskId?.[task.id];
        /* Resolved once in the framework — the same status the navigator's pill shows. */
        const overallStatus = overallTaskStatusByTaskId?.[task.id] ?? 'draft';
        const contractorName = getContractorForTask(task)?.name;
        const { product, qty, rate } = getTaskProductCells(task);
        const productLabel = product ? truncateGroupTaskListName(product, PRODUCT_LABEL_MAX_LENGTH) : undefined;
        const photoCount = task.optimized?.allPhotoIds?.length ?? 0;
        const noteCount = (scanNotesByScanPartId?.[task.id] ?? []).filter(note => note.body?.trim()).length;
        const isUnready = overallStatus === 'empty' || overallStatus === 'draft';
        const needsAttentionFlag = taskNeedsAttentionFlag(task, evalContext);
        const modifierLabel = getModifierLabel(task, knownTaskModifiers);
        const contractorFlagged = taskMatchesScopeAttentionFilter(task, 'unassignedContractors', evalContext);
        /** Always false today — no live stock data source exists yet (see
         *  scopeAttentionFilterLib's `outOfStock` case) — but wired the same way
         *  as the other two so it starts working the moment one does. */
        const productFlagged = taskMatchesScopeAttentionFilter(task, 'outOfStock', evalContext);
        const costFlagged = taskMatchesScopeAttentionFilter(task, 'changeOrders', evalContext);

        return (
          <button
            key={task.id}
            type='button'
            css={[TASK_ROW_STYLE, isUnready && UNREADY_TEXT_STYLE]}
            title={fullName}
            onClick={() => {
              if (!scanId) return;
              navigate(getScanUrl({
                scanId,
                groupId: group.id,
                taskId: task.id,
                focusPanel: 'edit',
                newScopeBuilderFlow,
              }));
            }}
          >
            <span css={TASK_NAME_STYLE}>
              <span css={NEEDS_ATTENTION_SLOT_STYLE}>
                <NeedsAttentionIndicator show={needsAttentionFlag} size={NEEDS_ATTENTION_ICON_SIZE} label={DEFAULT_NEEDS_ATTENTION_TITLE} />
              </span>
              <TaskStatusBadge status={overallStatus} iconOnly />
              <span css={TASK_NAME_TEXT_STYLE}>{displayName}</span>
            </span>
            <span
              css={[contractorName ? TASK_TEXT_CELL_STYLE : TASK_TEXT_CELL_NONE_STYLE, contractorFlagged && FLAGGED_TEXT_STYLE]}
              title={contractorFlagged ? 'Flag — Unassigned contractor' : contractorName ?? 'Unassigned'}
            >
              {contractorName ?? 'Unassigned'}
            </span>
            <span css={modifierLabel === '—' ? TASK_TEXT_CELL_NONE_STYLE : TASK_TEXT_CELL_STYLE} title={modifierLabel}>
              {modifierLabel}
            </span>
            <span
              css={[productLabel ? TASK_TEXT_CELL_STYLE : TASK_TEXT_CELL_NONE_STYLE, productFlagged && FLAGGED_TEXT_STYLE]}
              title={productFlagged ? 'Flag — Out of stock' : product ?? ''}
            >
              {productLabel ?? '—'}
            </span>
            <span css={TASK_NUM_CELL_STYLE}>{qty ?? '—'}</span>
            <span css={TASK_NUM_CELL_STYLE}>{rate ?? '—'}</span>
            <span css={TASK_FILES_CELL_STYLE} title={`${photoCount} photo${photoCount === 1 ? '' : 's'} · ${noteCount} note${noteCount === 1 ? '' : 's'}`}>
              <span css={photoCount ? undefined : FILES_ZERO_STYLE}>{photoCount}</span>
              <span css={FILES_SEP_STYLE}>/</span>
              <span css={noteCount ? undefined : FILES_ZERO_STYLE}>{noteCount}</span>
            </span>
            <span
              css={[TASK_COST_STYLE, costFlagged && FLAGGED_TEXT_STYLE]}
              title={costFlagged ? 'Flag — Change order' : undefined}
            >
              {formatUsd(getPartCostValue(task) ?? 0, false, true)}
            </span>
          </button>
        );
      })}
    </div>
  );
};
